import React, { useState, useEffect } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import CategorySelect from 'common/components/CategorySelect';
import { usePageCopilotPrompts } from 'areas/copilot/components/CopilotPromptsContext';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title as ChartTitle,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import {
  PageSection,
  ControlContainerRow,
  ControlContainer,
  Subtitle,
  StyledCard,
} from 'common/styles/page';
import { useApiPortalV1AssessmentsResultsListQuery } from 'areas/assessment-schedule/api';
import { hexToRgb } from 'common/styles/colors';
import { AggregateType } from 'common/constants/AggregateType';
import {
  getSelectedAssessmentPeriod,
  getSelectedAssessmentPeriodCategories,
  getSelectedAssessmentPeriodId,
} from 'common/selectors/auth';

ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTitle, Tooltip, Legend);

const AnalyticsDemographicsBar: React.FC = () => {
  const assessmentPeriodId = useSelector(getSelectedAssessmentPeriodId);
  const assessmentPeriod = useSelector(getSelectedAssessmentPeriod);
  const assessmentPeriodCategories = useSelector(
    getSelectedAssessmentPeriodCategories,
    shallowEqual
  ) as any[];

  const { data: data_by_category } = useApiPortalV1AssessmentsResultsListQuery({
    aggregateType: AggregateType.CATEGORY_OVERALL,
    assessmentPeriodId: assessmentPeriodId,
  });

  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const { setPagePrompts } = usePageCopilotPrompts();
  const [barData, setBarData] = useState<any[]>([]);
  const selectedCategoryName =
    assessmentPeriodCategories.find(category => category.id === selectedCategory)?.name ||
    'Selected Category';

  useEffect(() => {
    if (!assessmentPeriodCategories || assessmentPeriodCategories.length === 0) {
      setSelectedCategory(null);
      return;
    }

    const selectedCategoryExists = assessmentPeriodCategories.some(
      category => category.id === selectedCategory
    );
    if (selectedCategoryExists) return;

    const primaryCategory = assessmentPeriodCategories.find(
      category => category.priority === 'primary'
    );
    setSelectedCategory(primaryCategory?.id || assessmentPeriodCategories[0]?.id || null);
  }, [assessmentPeriodCategories, selectedCategory]);

  useEffect(() => {
    if (data_by_category && selectedCategory) {
      const filtered = data_by_category[0]?.result.aggregate_scores.filter(
        (s: any) => s.category_id === selectedCategory
      );
      const mapped = filtered.map((item: any) => ({
        label: item.option_name,
        score: parseFloat(item.overall_score.toFixed(1)),
        nValue: item.average_n,
        color: hexToRgb(item.color, 0.8),
      }));
      const sorted = mapped.sort(
        (a: { nValue: number }, b: { nValue: number }) => b.nValue - a.nValue
      );
      setBarData(sorted);
    }
  }, [data_by_category, selectedCategory]);

  useEffect(() => {
    const categoryLabel =
      assessmentPeriodCategories.find(category => category.id === selectedCategory)?.name ||
      'Selected Category';

    setPagePrompts([
      {
        title: 'Demographic Distribution',
        displayText: `Summarize the demographic distribution for ${categoryLabel}.`,
        prompt:
          `Review the demographic summary for ${categoryLabel} and explain which groups show the strongest participation, scores, or representation. Highlight the most important differences and what they mean for inclusion and engagement.`,
      },
      {
        title: 'Group Score Gaps',
        displayText: `Identify score gaps within ${categoryLabel}.`,
        prompt:
          `Identify the most significant score gaps between demographic groups for ${categoryLabel}. Explain which groups are underperforming, which are overperforming, and why those gaps should matter to leaders.`,
      },
      {
        title: 'Equity Actions',
        displayText: `Recommend equity actions for ${categoryLabel}.`,
        prompt:
          `Based on the demographic distribution for ${categoryLabel}, recommend actions leaders should take to improve equity, participation, and support for underrepresented groups.`,
      },
    ]);

    return () => setPagePrompts([]);
  }, [setPagePrompts, assessmentPeriodCategories, selectedCategory]);

  const chartData = {
    labels: barData.map(d => d.label),
    datasets: [
      {
        label: 'Respondents',
        data: barData.map(d => d.nValue),
        backgroundColor: barData.map(d => d.color),
        yAxisID: 'y1',
        barThickness: 40,
      },
      {
        label: 'Overall Score',
        data: barData.map(d => d.score),
        backgroundColor: 'rgba(77, 176, 233, 0.8)',
        yAxisID: 'y',
        barThickness: 10,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
      },
      tooltip: {
        callbacks: {
          label: (context: any) => `${context.dataset.label}: ${context.formattedValue}`,
        },
      },
    },
    scales: {
      y: {
        type: 'linear' as const,
        position: 'right' as const,
        min: 0,
        max: 10,
        title: {
          display: true,
          text: 'Overall Score (0-10)',
        },
      },
      y1: {
        type: 'linear' as const,
        position: 'left' as const,
        grid: {
          drawOnChartArea: false,
        },
        title: {
          display: true,
          text: 'Respondent Count',
        },
      },
    },
  };

  return (
    <PageSection
      padright
      width="100%"
      display="flex"
      alignItems="flex-start"
      gap="10px"
      style={{ height: '100%' }}
    >
      <StyledCard>
        <ControlContainerRow padbottom="10px" padright="10px" padleft="20px" padtop="10px">
          <ControlContainer justify="left">
            <Subtitle>{`${selectedCategoryName} Scores vs. Organization Overall`}</Subtitle>
          </ControlContainer>
          <ControlContainer justify="right">
            <CategorySelect
              categories={assessmentPeriodCategories as any}
              selectedCategory={selectedCategory}
              onCategoryChange={setSelectedCategory}
              placeholder="Select Category"
              width="250px"
            />
          </ControlContainer>
        </ControlContainerRow>

        <PageSection padbottom padleft padright style={{ width: '100%', height: '450px' }}>
          <Bar data={chartData} options={chartOptions} />
        </PageSection>
      </StyledCard>
    </PageSection>
  );
};

export default AnalyticsDemographicsBar;

